import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {MutationObserver | null}
   */
  #headerMutationObserver = null;

  /**
   * When the pointer leaves the overflow-list host (including its padding), run deactivate.
   * Top-level list item pointerleave can fire while the pointer is still over this host; this
   * second hook closes the menu once the pointer exits the whole nav host.
   */
  #onOverflowListPointerLeave = () => {
    this.#deactivate();
  };

  #onWindowScroll = () => {
    this.#closeActiveMenuImmediately();
  };

  #onWindowWheel = () => {
    this.#closeActiveMenuImmediately();
  };

  /**
   * @param {PointerEvent | MouseEvent} event
   */
  #onMenuLinkActivate = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.mega-menu a, .mega-menu__promo-item');
    if (!link) return;

    // remove transparent state so header renders in default (homepage) style
    if (this.headerComponent) {
      this.headerComponent.removeAttribute('transparent');
      this.headerComponent.removeAttribute('data-transparent-mode');
      this.headerComponent.setAttribute('data-menu-click-closing', 'true');
    }

    this.#closeActiveMenuImmediately();
  };

  #isMenuHovered = () => {
    return this.matches(':hover') || Boolean(this.querySelector(':hover'));
  };

  #maybeCloseOnHeaderReveal = () => {
    if (this.ariaExpanded !== 'true') return;
    if (this.#isMenuHovered()) return;

    const headerEl = this.headerComponent;
    if (!headerEl) return;

    const scrollDirection = headerEl.getAttribute('data-scroll-direction');
    const stickyState = headerEl.getAttribute('data-sticky-state');

    if (scrollDirection === 'up' || stickyState === 'active') {
      this.#deactivate();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    this.refs.overflowMenu?.addEventListener('pointerleave', this.#onOverflowListPointerLeave);
    if (this.headerComponent) {
      this.#headerMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.attributeName === 'data-scroll-direction' ||
            mutation.attributeName === 'data-sticky-state'
          ) {
            this.#maybeCloseOnHeaderReveal();
          }
        });
      });
      this.#headerMutationObserver.observe(this.headerComponent, {
        attributes: true,
        attributeFilter: ['data-scroll-direction', 'data-sticky-state'],
      });
      this.#maybeCloseOnHeaderReveal();
    }
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    window.addEventListener('scroll', this.#onWindowScroll, { passive: true });
    window.addEventListener('wheel', this.#onWindowWheel, { passive: true });
    this.addEventListener('pointerdown', this.#onMenuLinkActivate, true);
    this.addEventListener('click', this.#onMenuLinkActivate, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('scroll', this.#onWindowScroll);
    window.removeEventListener('wheel', this.#onWindowWheel);
    this.removeEventListener('pointerdown', this.#onMenuLinkActivate, true);
    this.removeEventListener('click', this.#onMenuLinkActivate, true);
    this.refs.overflowMenu?.removeEventListener('pointerleave', this.#onOverflowListPointerLeave);
    this.#headerMutationObserver?.disconnect();
    this.#headerMutationObserver = null;
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow-list host (ref overflowMenu) is hovered — including its padding.
   * Used to keep the menu open while moving within that host; leaving the host triggers
   * #onOverflowListPointerLeave so #deactivate still runs afterward.
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const hasDropdown = Boolean(submenu);
    this.ariaExpanded = hasDropdown ? 'true' : 'false';
    if (item.getAttribute('aria-haspopup') === 'true') {
      item.ariaExpanded = hasDropdown ? 'true' : 'false';
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', hasDropdown ? '1' : '0');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Force-close the active submenu regardless of hover state.
   */
  #closeActiveMenuImmediately = () => {
    const item = this.#state.activeItem;
    if (!item || item != this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
